/**
 * One WebGLRenderer, one canvas, several views (the 3D theater and the three MPR panes) drawn
 * into the rectangles of their DOM elements with scissor/viewport. Rendering is on demand:
 * views are marked dirty by whoever changes them; a continuous loop runs only while something
 * animates. With `reduceWhileInteracting` on (a Theater panel toggle, off by default, remembered
 * per device),
 * interaction drops the DVR quality (256 steps, trilinear) and the rest frame restores the
 * preset quality. Resolution is then adaptive: dragging starts at full resolution and drops to
 * half as soon as interactive frames miss the budget; the drop sticks for the session and is
 * remembered per device, so a slow GPU never pays the full-resolution probe twice.
 */
import * as THREE from 'three';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { CameraControls } from './CameraControls';
import { MM_PER_UNIT } from './frame';
import type { VolumeObject } from '@/volume/VolumeObject';

export interface View {
  id: string;
  element: HTMLElement;
  camera: THREE.Camera;
  layer: number;
  dirty: boolean;
  /** Called before the view renders (update uniforms, overlays...). */
  before?: (rect: ViewRect) => void;
  /** Called after the view renders (2D overlays). */
  after?: (rect: ViewRect) => void;
  background: THREE.Color;
}

export interface ViewRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const LAYER_MAIN = 0;
export const LAYER_SLICE_BASE = 1; // slices use layers 1, 2, 3
export const LAYER_OVERLAY = 4; // measurement overlays (drawn in the 3D view, hidden from slices)

interface QualitySettings {
  samplingMultiplier: number;
  cubic: boolean;
}

export class Viewer {
  readonly renderer: THREE.WebGLRenderer;
  readonly css2d: CSS2DRenderer;
  readonly scene = new THREE.Scene();
  /** Scene content in mm, Unity axes; the group applies the mm→unit scale and the z flip. */
  readonly root = new THREE.Group();
  readonly controls: CameraControls;
  readonly views = new Map<string, View>();
  volume: VolumeObject | null = null;
  restQuality: QualitySettings = { samplingMultiplier: 3, cubic: true };
  interactiveQuality: QualitySettings = { samplingMultiplier: 1, cubic: false };
  /** Resolution scale interactive frames fall back to once they prove too slow. */
  interactiveResolutionScale = 0.5;
  /** Interactive frames slower than this (ms, two in a row) trigger the resolution drop. */
  interactiveFrameBudgetMs = 20;
  onRendered: ((viewId: string) => void) | null = null;

  private canvas: HTMLCanvasElement;
  private container: HTMLElement;
  private raf = 0;
  private lastTime = 0;
  private interacting = false;
  private reduce = false;
  private interactiveScale = 1;
  private slowFrames = 0;
  private measureFrom = 0;
  private lastRendered = false;
  private animating = false;
  private disposed = false;
  private resizeObserver: ResizeObserver;
  private mainView: View;
  private headlight: THREE.DirectionalLight;

  constructor(container: HTMLElement, mainViewElement: HTMLElement, labelContainer: HTMLElement) {
    this.container = container;
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'viewer-canvas';
    container.prepend(this.canvas);
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      premultipliedAlpha: true,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.autoClear = false;
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setScissorTest(true);
    this.renderer.sortObjects = true;

    this.css2d = new CSS2DRenderer({ element: labelContainer });
    labelContainer.style.position = 'absolute';
    labelContainer.style.inset = '0';
    labelContainer.style.pointerEvents = 'none';
    labelContainer.style.overflow = 'hidden';

    this.root.scale.set(1 / MM_PER_UNIT, 1 / MM_PER_UNIT, -1 / MM_PER_UNIT);
    this.root.name = 'root';
    this.scene.add(this.root);
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x333344, 0.9));
    this.headlight = new THREE.DirectionalLight(0xffffff, 1.4);
    this.scene.add(this.headlight);
    this.scene.add(this.headlight.target);

    this.controls = new CameraControls(mainViewElement);
    this.controls.camera.layers.set(LAYER_MAIN);
    this.controls.camera.layers.enable(LAYER_OVERLAY);
    this.controls.perspective.layers.set(LAYER_MAIN);
    this.controls.perspective.layers.enable(LAYER_OVERLAY);
    this.controls.orthographic.layers.set(LAYER_MAIN);
    this.controls.orthographic.layers.enable(LAYER_OVERLAY);
    this.controls.onChange = () => this.invalidate('main');
    this.controls.onInteraction = (active) => this.setInteracting(active);

    this.mainView = {
      id: 'main',
      element: mainViewElement,
      camera: this.controls.camera,
      layer: LAYER_MAIN,
      dirty: true,
      background: new THREE.Color(0x000000),
    };
    this.views.set('main', this.mainView);

    this.reduce = readStoredReduce();
    const stored = readStoredScale();
    if (stored !== null && stored < 1) this.interactiveScale = this.interactiveResolutionScale;

    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(container);
    this.onResize();
    this.loop(0);
  }

  // ------------------------------------------------------------ views
  addView(view: View): void {
    this.views.set(view.id, view);
    view.dirty = true;
    this.requestFrame();
  }

  removeView(id: string): void {
    this.views.delete(id);
  }

  invalidate(id?: string): void {
    if (id) {
      const v = this.views.get(id);
      if (v) v.dirty = true;
    } else {
      for (const v of this.views.values()) v.dirty = true;
    }
    this.requestFrame();
  }

  setAnimating(on: boolean): void {
    this.animating = on;
    if (on) this.requestFrame();
  }

  /** Whether camera interaction renders at the reduced interactive quality and resolution. */
  get reduceWhileInteracting(): boolean {
    return this.reduce;
  }

  set reduceWhileInteracting(on: boolean) {
    if (this.reduce === on) return;
    this.reduce = on;
    storeReduce(on);
    this.slowFrames = 0;
    this.measureFrom = 0;
    this.applyQuality();
    this.invalidate('main');
  }

  private setInteracting(active: boolean): void {
    if (this.interacting === active) return;
    this.interacting = active;
    this.slowFrames = 0;
    this.measureFrom = 0;
    this.applyQuality();
    this.invalidate('main');
  }

  /** Called once per interactive frame with the time since the previous rendered one. */
  private noteInteractiveFrame(ms: number): void {
    if (this.interactiveScale <= this.interactiveResolutionScale) return;
    this.slowFrames = ms > this.interactiveFrameBudgetMs ? this.slowFrames + 1 : 0;
    if (this.slowFrames < 2) return;
    this.interactiveScale = this.interactiveResolutionScale;
    storeScale(this.interactiveScale);
    this.applyQuality();
    this.invalidate('main');
  }

  applyQuality(): void {
    const reduced = this.interacting && this.reduce;
    const q = reduced ? this.interactiveQuality : this.restQuality;
    if (this.volume) {
      this.volume.setSamplingMultiplier(q.samplingMultiplier);
      this.volume.setCubicInterpolationEnabled(q.cubic);
    }
    const dpr = window.devicePixelRatio * (reduced ? this.interactiveScale : 1);
    if (Math.abs(this.renderer.getPixelRatio() - dpr) > 1e-3) this.renderer.setPixelRatio(dpr);
  }

  // ------------------------------------------------------------ frame loop
  private requestFrame(): void {
    if (this.raf || this.disposed) return;
    this.raf = requestAnimationFrame((t) => this.loop(t));
  }

  private loop(time: number): void {
    this.raf = 0;
    if (this.disposed) return;
    const dt = this.lastTime ? Math.min(0.1, (time - this.lastTime) / 1000) : 0;
    this.lastTime = time;
    const moving = this.controls.update(dt);
    if (this.interacting && this.reduce) {
      if (this.measureFrom && this.lastRendered) this.noteInteractiveFrame(time - this.measureFrom);
      this.measureFrom = time;
    }
    this.lastRendered = this.renderDirty();
    if (moving || this.animating || this.interacting) this.requestFrame();
  }

  private onResize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    if (w === 0 || h === 0) return;
    this.renderer.setSize(w, h, false);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.css2d.setSize(this.mainView.element.clientWidth, this.mainView.element.clientHeight);
    this.invalidate();
  }

  rectOf(el: HTMLElement): ViewRect {
    const r = el.getBoundingClientRect();
    const c = this.canvas.getBoundingClientRect();
    return {
      x: r.left - c.left,
      y: c.bottom - r.bottom,
      width: r.width,
      height: r.height,
    };
  }

  /** The drawing buffer is not preserved between frames, so whenever any view changed every
   * visible view is redrawn; the canvas is cleared to transparent first so the DOM shows
   * through outside the view rectangles. */
  private renderDirty(): boolean {
    this.mainView.camera = this.controls.camera;
    if (![...this.views.values()].some((v) => v.dirty)) return false;
    this.renderer.setScissorTest(false);
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.clear(true, true, true);
    this.renderer.setScissorTest(true);
    for (const view of this.views.values()) {
      view.dirty = false;
      const el = view.element;
      if (!el.isConnected || el.clientWidth === 0 || el.clientHeight === 0) continue;
      this.renderView(view);
    }
    return true;
  }

  renderView(view: View): void {
    const rect = this.rectOf(view.element);
    if (rect.width <= 0 || rect.height <= 0) return;
    const cam = view.camera;
    if ((cam as THREE.PerspectiveCamera).isPerspectiveCamera) {
      const pc = cam as THREE.PerspectiveCamera;
      if (view.id === 'main') this.controls.setAspect(rect.width / rect.height);
      else {
        pc.aspect = rect.width / rect.height;
        pc.updateProjectionMatrix();
      }
    } else if (view.id === 'main') {
      this.controls.setAspect(rect.width / rect.height);
    }
    view.before?.(rect);
    if (view.id === 'main') {
      this.headlight.position.copy(cam.position);
      this.headlight.target.position
        .copy(cam.position)
        .add(new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion));
      this.headlight.target.updateMatrixWorld();
    }
    this.volume?.updateCamera(cam);
    this.renderer.setViewport(rect.x, rect.y, rect.width, rect.height);
    this.renderer.setScissor(rect.x, rect.y, rect.width, rect.height);
    this.renderer.setClearColor(view.background, 1);
    this.renderer.clear(true, true, true);
    this.renderer.render(this.scene, cam);
    if (view.id === 'main') {
      this.css2d.setSize(rect.width, rect.height);
      this.css2d.render(this.scene, cam);
    }
    view.after?.(rect);
    this.onRendered?.(view.id);
  }

  /** Renders the main view into an offscreen target and returns a JPEG blob. */
  async capture(width: number, height: number, quality = 0.85): Promise<Blob> {
    const target = new THREE.WebGLRenderTarget(width, height, { samples: 4 });
    const cam = this.controls.camera;
    const prevAspect = this.controls.perspective.aspect;
    this.controls.setAspect(width / height);
    const prevInteracting = this.interacting;
    this.interacting = false;
    this.applyQuality();
    this.volume?.updateCamera(cam);
    this.renderer.setRenderTarget(target);
    this.renderer.setViewport(0, 0, width, height);
    this.renderer.setScissor(0, 0, width, height);
    this.renderer.setClearColor(0x000000, 1);
    this.renderer.clear(true, true, true);
    this.renderer.render(this.scene, cam);
    const pixels = new Uint8Array(width * height * 4);
    this.renderer.readRenderTargetPixels(target, 0, 0, width, height, pixels);
    this.renderer.setRenderTarget(null);
    target.dispose();
    this.controls.setAspect(prevAspect);
    this.interacting = prevInteracting;
    this.applyQuality();
    this.invalidate('main');
    const c = document.createElement('canvas');
    c.width = width;
    c.height = height;
    const ctx = c.getContext('2d')!;
    const img = ctx.createImageData(width, height);
    // flip vertically (GL origin is bottom-left)
    for (let y = 0; y < height; y++) {
      const src = (height - 1 - y) * width * 4;
      img.data.set(pixels.subarray(src, src + width * 4), y * width * 4);
    }
    ctx.putImageData(img, 0, 0);
    return new Promise((resolve, reject) =>
      c.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', quality),
    );
  }

  /** World AABB (Unity units) of the given objects. */
  worldBox(objects: THREE.Object3D[]): THREE.Box3 {
    const box = new THREE.Box3();
    for (const o of objects) {
      o.updateMatrixWorld(true);
      box.expandByObject(o, true);
    }
    return box;
  }

  dispose(): void {
    this.disposed = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.resizeObserver.disconnect();
    this.controls.dispose();
    this.renderer.dispose();
    this.canvas.remove();
  }
}

const SCALE_KEY = 'valvia.interactiveScale';

/** The reduced interactive scale this device settled on in an earlier session, if any. */
function readStoredScale(): number | null {
  try {
    const v = localStorage.getItem(SCALE_KEY);
    return v === null ? null : Number(v);
  } catch {
    return null;
  }
}

function storeScale(scale: number): void {
  try {
    localStorage.setItem(SCALE_KEY, String(scale));
  } catch {
    /* private mode or blocked storage: the drop still applies for this session */
  }
}

const REDUCE_KEY = 'valvia.reduceWhileInteracting';

function readStoredReduce(): boolean {
  try {
    return localStorage.getItem(REDUCE_KEY) === '1';
  } catch {
    return false;
  }
}

function storeReduce(on: boolean): void {
  try {
    localStorage.setItem(REDUCE_KEY, on ? '1' : '0');
  } catch {
    /* the toggle still applies for this session */
  }
}
